import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Popover,
  Pagination,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows, getPaymentStatusStyle, getServiceStyle } from "./payments.styles";
import NoDataFound from "../../Reports/NoDataFound";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaymentFilters from "./PaymentFilters";

const TransactionHistory = ({ data = [], onFilter, onRefresh, loading = false }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState(null);
  const rowsPerPage = 10;

  // Use data from props (fetched from backend)
  const transactions = data;

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 14,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2.5,
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleApplyFilters = (filters) => {
    // All filtering is done client-side, no need to refetch
    setActiveFilters(filters);
    setPage(1);
    handleFilterClose();
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const filteredTransactions = transactions.filter((t) => {
    // Search filter
    if (searchQuery && !t.clientName.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Apply active filters if set
    if (activeFilters) {
      // Date range filter (client-side)
      if (activeFilters.startDate && activeFilters.endDate && t.rawDate) {
        const startDate = new Date(activeFilters.startDate);
        const endDate = new Date(activeFilters.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        if (t.rawDate < startDate || t.rawDate > endDate) {
          return false;
        }
      }

      // Service type filter - only filter if service type exists
      if (activeFilters.serviceTypes && t.serviceType) {
        const selectedServices = Object.entries(activeFilters.serviceTypes)
          .filter(([, selected]) => selected)
          .map(([service]) => service);
        // Normalize service type: "hair care" -> "hair", "skin care" -> "skin"
        const normalizedServiceType = t.serviceType.split(" ")[0];
        if (selectedServices.length > 0 && !selectedServices.includes(normalizedServiceType)) {
          return false;
        }
      }

      // Payment method filter
      if (activeFilters.paymentMethod && activeFilters.paymentMethod !== "all") {
        const methodToCheck = t.method?.toLowerCase() || "";
        if (methodToCheck && methodToCheck !== activeFilters.paymentMethod.toLowerCase()) {
          return false;
        }
      }

      // Amount range filter (based on order cost)
      if (activeFilters.amountRange) {
        const min = parseFloat(activeFilters.amountRange.min) || 0;
        const max = parseFloat(activeFilters.amountRange.max) || Infinity;
        if (t.orderCost < min || t.orderCost > max) {
          return false;
        }
      }

      // Payment status filter
      if (activeFilters.paymentStatus && activeFilters.paymentStatus !== "all") {
        if (t.status !== activeFilters.paymentStatus) {
          return false;
        }
      }
    }

    return true;
  });

  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Detailed Transaction History
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search client or transaction..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.borderLight,
                },
                "&:hover fieldset": {
                  borderColor: colors.border,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
          <IconButton
            onClick={handleFilterClick}
            sx={{
              color: filterAnchor ? primaryColor : colors.textMuted,
              border: `1px solid ${filterAnchor ? primaryColor : colors.borderLight}`,
              borderRadius: 2,
              px: 1.5,
              "&:hover": {
                bgcolor: colors.bgPrimary,
                borderColor: primaryColor,
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 18 }} />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                ml: 0.75,
                color: filterAnchor ? primaryColor : colors.textSecondary,
              }}
            >
              Filter
            </Typography>
          </IconButton>
          {activeFilters && (
            <IconButton
              onClick={() => {
                setActiveFilters(null);
                setPage(1);
              }}
              sx={{
                color: colors.textMuted,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 2,
                px: 1.5,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                  borderColor: primaryColor,
                  color: primaryColor,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textSecondary,
                }}
              >
                Clear
              </Typography>
            </IconButton>
          )}
          {onRefresh && (
            <IconButton
              onClick={() => {
                setActiveFilters(null);
                setPage(1);
                onRefresh();
              }}
              disabled={loading}
              sx={{
                color: colors.textMuted,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 2,
                px: 1.5,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                  borderColor: primaryColor,
                  color: primaryColor,
                },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
          },
        }}
      >
        <PaymentFilters onApply={handleApplyFilters} onClose={handleFilterClose} />
      </Popover>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Date</TableCell>
            <TableCell sx={headerCellSx}>Client Name</TableCell>
            <TableCell sx={headerCellSx}>Service Type</TableCell>
            <TableCell sx={headerCellSx}>Order Cost</TableCell>
            <TableCell sx={headerCellSx}>Paid</TableCell>
            <TableCell sx={headerCellSx}>Pending</TableCell>
            <TableCell sx={headerCellSx}>Method</TableCell>
            <TableCell sx={headerCellSx}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedTransactions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ border: "none" }}>
                <NoDataFound />
              </TableCell>
            </TableRow>
          ) : paginatedTransactions.map((row) => {
            const statusStyle = getPaymentStatusStyle(row.status);
            const serviceStyle = getServiceStyle(row.serviceType);

            return (
              <TableRow
                key={row.id}
                sx={{
                  "&:hover": {
                    bgcolor: colors.bgPrimary,
                  },
                }}
              >
                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.textPrimary,
                    }}
                  >
                    {row.date}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                      src={row.clientAvatar}
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: primaryColor,
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(row.clientName)}
                    </Avatar>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      {row.clientName}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Chip
                    label={serviceStyle.label}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: serviceStyle.bg,
                      color: serviceStyle.color,
                      "& .MuiChip-label": {
                        px: 1.5,
                      },
                    }}
                  />
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.textPrimary,
                    }}
                  >
                    ₹{(row.orderCost || 0).toLocaleString()}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#22C55E",
                    }}
                  >
                    ₹{(row.paid || 0).toLocaleString()}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: row.pending > 0 ? "#EF4444" : colors.textSecondary,
                    }}
                  >
                    ₹{(row.pending || 0).toLocaleString()}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    {row.method || "-"}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Chip
                    label={statusStyle.label}
                    size="small"
                    icon={
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: statusStyle.color,
                          ml: 1,
                        }}
                      />
                    }
                    sx={{
                      height: 24,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: statusStyle.bg,
                      color: statusStyle.color,
                      "& .MuiChip-label": {
                        px: 1,
                      },
                      "& .MuiChip-icon": {
                        ml: 0.5,
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Footer */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={3}
        pt={2}
        borderTop={`1px solid ${colors.borderLight}`}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: colors.textMuted,
          }}
        >
          Showing {filteredTransactions.length > 0 ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filteredTransactions.length)} of {filteredTransactions.length} transactions
        </Typography>

        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={handlePageChange}
          size="small"
          sx={{
            "& .MuiPaginationItem-root": {
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSecondary,
              "&.Mui-selected": {
                bgcolor: primaryColor,
                color: "#fff",
                "&:hover": {
                  bgcolor: colors.primaryDark,
                },
              },
            },
          }}
        />
      </Stack>
    </Box>
  );
};

export default TransactionHistory;
